import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

import ArrayEditor from '../editors/ArrayEditor';
import {getConfigShapeAtConfigShapePath} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  BACKEND_FIELD_GENERATOR_CONFIG,
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
} from '../StepConfigEditorTypes';

function HamiltonRuntimeParametersEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const runtimeParamsShape = getConfigShapeAtConfigShapePath(['runtimeParameters'], configShape, config);

  // VENUS exposes no way to introspect a method's variables, so runtime parameters are always a
  // free-form list of name/value pairs sent to PUT /execution/input at run time. The list starts
  // unset (no value in config), so we render ArrayEditor whenever the shape is an array and let it
  // default an absent value to an empty list — that surfaces the "add parameter" control.
  if (runtimeParamsShape.kind === 'array') {
    return (
      <ArrayEditor
        config={config}
        configShape={runtimeParamsShape}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
        configShapePath={['runtimeParameters']}
        addButtonAltText="Add parameter"
        elementUISchema={{
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['name', 'value'],
          [NUM_COLUMNS_KEY]: 2,
          name: {
            displayName: 'Parameter',
            configShapePath: ['name'],
          },
          value: {
            displayName: 'Value',
            configShapePath: ['value'],
          },
        }}
      />
    );
  }

  return <></>;
}

export const RUN_HAMILTON_METHOD_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_HAMILTON_METHOD,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['connectionId', 'methodFilePath', 'simulation', 'runtimeParameters'],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['connectionId']},
  connectionId: {
    displayName: 'Connection',
    infoText: 'The instrument connection to the Hamilton VENUS system.',
    configShapePath: ['connectionId'],
  },
  methodFilePath: {
    displayName: 'Method file path',
    infoText: 'The full path to the method file on the Hamilton VENUS PC.',
    configShapePath: ['methodFilePath'],
  },
  simulation: {
    displayName: 'Simulation mode',
    infoText: 'Run the method in VENUS simulation mode without moving hardware.',
    configShapePath: ['simulation'],
  },
  runtimeParameters: {
    displayName: 'Runtime parameters',
    infoText: 'Method variables sent to the VENUS system at run time.',
    [CUSTOM_COMPONENT_KEY]: HamiltonRuntimeParametersEditor,
  },
};
